import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DealerHierarchyService {

    public baseUrl = `${environment.sspLicenseApi.url}`
    dealerNumber: string = null;
    regionName: string = null;
    divisionNumber: string = null;
    divisionId: string = null;
    divisionName: string = null;
    customersMovedEvent=new Subject<void>();
    selectedDivisionName = new BehaviorSubject<string>('');
    selectedSbsBranchName = new BehaviorSubject<string>('');
    dealerName = new BehaviorSubject<string>(null);
    dealerHqName = new BehaviorSubject<string>(null);
    private customerNameList = new Subject<any>();
    customerNameList$ = this.customerNameList.asObservable();

    setCustomerNameListData(data: any) {
        this.customerNameList.next(data);
    }
    constructor(public http: HttpClient) {
    }

    getAllHQs(searchTerm: string = null, pageToken: string = null) {
        let url = `${this.baseUrl}/hierarchy/allHQs`;

        if (searchTerm) {
            if ((searchTerm.toLocaleLowerCase() === 'sbs' || searchTerm.toLocaleLowerCase() === 'sbs hq') && environment.stackRegion=='US') {
                searchTerm = "Sharp Business Systems HQ";
            }
            url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        let headers = new HttpHeaders();
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }

        return this.http.get(url, { headers });
    }

    getSBSBranches(searchTerm: string = null, pageToken: string = null) {
        let url = `${this.baseUrl}/hierarchy/sbsBranches`;
        if (searchTerm) {
            url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        let headers = new HttpHeaders().set('page-size', '100');
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }
        return this.http.get(url, { headers });
    }
    getRegionDealers(selectedRegion, searchTerm: string = null, pageToken: string = null) {
        let queryParams = "";
        if (selectedRegion){
            queryParams = `regionName=${selectedRegion}`
        }
        if (searchTerm) {
            queryParams += `${queryParams ? '&' : ''}searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        let headers = new HttpHeaders().set('page-size', '100');
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }
     
        return this.http.get(`${this.baseUrl}/hierarchy/regionDealers?${queryParams}`, { headers });
    }
    getSBSBranchCustomers(dealerNumber: string, targetAuthConn: string = null, pageToken = null, searchTerm: string = null) {
        let url = `${this.baseUrl}/hierarchy/sbsCustomers`;
        let headers = new HttpHeaders({
            dealerNumber: dealerNumber,
            'page-size': '100'
        });
        if (searchTerm) {
            url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
        }        
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }
        if (targetAuthConn) {
            headers = headers.set('targetAuthConn', targetAuthConn);
        }
        return this.http.get(url, { headers });
    }
    sbsBranchCustomers(dealerNumber: string, targetAuthConn: string = null) {
        let url = `${this.baseUrl}/hierarchy/sbsCustomers`;
        let headers = new HttpHeaders({
            dealerNumber: dealerNumber,            
        });
        if (targetAuthConn) {
            headers = headers.set('targetAuthConn', targetAuthConn);
        }
        return this.http.get(url, { headers });
    }

    getDealerDivisionCustomers(dealerNumber: string, targetAuthConn: string = null, divisionId: string = null) {
        let headers = new HttpHeaders({
            dealerNumber: dealerNumber
        });
        let queryParams = "";
        if (targetAuthConn) {
            queryParams += `targetAuthConn=${targetAuthConn}&`;
        }
        if (divisionId) {
            queryParams += `divisionId=${divisionId}`;
        }
        return this.http.get(`${this.baseUrl}/hierarchy/dealerCustomers?${queryParams}`, { headers });
    }
    getDealerCustomers(dealerNumber: string, targetAuthConn: string = null, divisionId: string = null, searchTerm: string = null, pageToken: string = null) {
        let headers = new HttpHeaders({
            dealerNumber: dealerNumber,
            'page-size': '100'
        });
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }
        let queryParams = "";
        if (targetAuthConn) {
            queryParams += `targetAuthConn=${targetAuthConn}&`;
        }
        if (divisionId) {
            queryParams += `divisionId=${divisionId}`;
        }
        if (searchTerm) {
            queryParams += `&searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        return this.http.get(`${this.baseUrl}/hierarchy/dealerCustomers?${queryParams}`, { headers });
    }

    getDealerDivisions(dealerNumber: string, divisionId?: string, searchTerm: string = null, pageToken = null) {
        let url = `${this.baseUrl}/hierarchy/dealerDivisions/${dealerNumber}`;

        if (divisionId) {
            url += `?divisionId=${divisionId}`;
        }
        if (searchTerm) {
            const separator = divisionId ? '&' : '?';
            url += `${separator}searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        let headers = new HttpHeaders().set('page-size', '100');;
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }

        return this.http.get(url, { headers });
    }

    /**
     * Fetches the MICAS customer list for the given dealer number. Pagination is
     * header-based (page-size/page-token) and the response carries a
     * `continuationToken` for the next page - same pattern as getDealerCustomers.
     *
     * @param dealerNumber Dealer number to list MICAS customers for.
     * @param searchTerm Keyword to filter customers by, or null for all.
     * @param pageToken Continuation token for the next page (null for the first page).
     */
    getMicasCustomers(dealerNumber: string, searchTerm: string = null, pageToken: string = null) {
        let url = `${this.baseUrl}/hierarchy/micasCustomers`;
        if (searchTerm) {
            url += `?searchTerm=${encodeURIComponent(searchTerm)}`;
        }
        let headers = new HttpHeaders({
            dealerNumber: dealerNumber
        }).set('page-size', '100');
        if (pageToken) {
            headers = headers.set('page-token', pageToken);
        }
        return this.http.get(url, { headers });
    }

    moveCustomersAcrossDivisions(accessToken:any, movingCustomersInfo:any) {
        const URI = `${this.baseUrl}/divisions/movecustomers`;
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'token': 'Bearer ' + accessToken,
            }),
        };
        const body = movingCustomersInfo;
        return this.http.post<any>(URI, body, httpOptions);

    }

}